import React, { Dispatch, useEffect, useState } from 'react';
import { SortDirection, v2_parcelSearchEvent, v2_parcelSearchResult } from '@lanterds/types';
import { Box, Typography, Card, CardContent, Tooltip, Button } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import FmtpTable from 'src/components/common/FmtpTable/FmtpTable';
import LoadingIndicator from 'src/components/common/LoadingIndicator/LoadingIndicator';
import { ParcelEndpointInfo, v2_sortBy } from '@lanterds/types/build/parcelSearch';
import { ColumnDef, Header } from '@tanstack/react-table';
import { formatAndHandleUtcFallback, formatEndpointAddress } from 'src/util/formatters';
import { ParcelCode } from './ParcelCode';
import InfoTwoToneIcon from '@mui/icons-material/InfoTwoTone';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
type ResultsGridProps = {
    parcels: v2_parcelSearchResult[] | null;
    isFetchingParcels: boolean;
    handleSortClick: (column: v2_sortBy, direction: SortDirection) => void;
    setShouldCondenseForm: Dispatch<React.SetStateAction<boolean>>;
    sortColumn: v2_sortBy;
    sortDirection: SortDirection;
};

type ParcelSearchGridData = v2_parcelSearchResult & {
    customerName: string;
    isDistress: 'Yes' | 'No';
    endPointAddress: string;
};

const toolTipMessages = {
    dates: 'All times are local unless otherwise specified',
    parcelCode: 'Click the parcel code to see the parcel details',
};

const SCROLL_POSITION_CONDENSE_THRESHOLD = 100;
const SCROLL_POSITION_EXPAND_THRESHOLD = SCROLL_POSITION_CONDENSE_THRESHOLD * 3;
const SCROLL_POSITION_TOP = 0;

export function ParcelSearchResultsGrid({
    parcels,
    isFetchingParcels,
    handleSortClick,
    setShouldCondenseForm,
    sortColumn,
    sortDirection,
}: ResultsGridProps) {
    const [selectedRows, setSelectedRows] = useState({});
    const [ableToExpand, setAbleToExpand] = useState(false);

    useEffect(() => {
        if (parcels && !parcels.length) {
            enqueueSnackbar('No results for your search.', {
                variant: 'info',
                preventDuplicate: true,
                action: (key) => (
                    <Button size="small" className="dismiss-button" onClick={() => closeSnackbar(key)}>
                        Dismiss
                    </Button>
                ),
            });
        }
    }, []);

    const updateFormSizeHandler = React.useCallback(
        async (containerRefElement?: HTMLDivElement | null) => {
            if (containerRefElement) {
                const scrollPosition = containerRefElement.scrollTop;

                if (scrollPosition > SCROLL_POSITION_CONDENSE_THRESHOLD) {
                    setShouldCondenseForm(true);
                }

                if (scrollPosition > SCROLL_POSITION_EXPAND_THRESHOLD) {
                    setAbleToExpand(true);
                }

                if (ableToExpand && scrollPosition === SCROLL_POSITION_TOP) {
                    setShouldCondenseForm(false);
                    setAbleToExpand(false);
                }
            }
        },
        [ableToExpand],
    );

    const onSortChange = (header: Header<ParcelSearchGridData, unknown>) => {
        const { column } = header;
        const { columnDef } = column;
        const { meta } = columnDef ?? {};

        let direction = SortDirection.ASCENDING;

        let sortName = column.id as v2_sortBy;

        switch (column.id) {
            case 'possessionDate':
            case 'deliveryDate':
                if (meta?.sortingOptionName) {
                    sortName = meta?.sortingOptionName as v2_sortBy;
                }
                break;
            default:
                break;
        }

        if (sortName.toLowerCase() === sortColumn?.toLowerCase() && sortDirection === SortDirection.ASCENDING) {
            direction = SortDirection.DESCENDING;
        }

        header.column.toggleSorting(direction === SortDirection.DESCENDING);

        closeSnackbar();

        handleSortClick(sortName, direction);
    };

    const mapData = (parcels: v2_parcelSearchResult[] | null): ParcelSearchGridData[] => {
        if (parcels) {
            return parcels?.map((parcel) => {
                const formattedPossessionDate = // if possessionDate and deliveryDate are the same, do not show possession date
                    new Date(parcel?.possessionDateUtc ?? parcel?.possessionDateLocal ?? 0).getTime() ===
                    new Date(parcel?.deliveryDateUtc ?? parcel?.deliveryDateLocal ?? 0).getTime()
                        ? ''
                        : formatAndHandleUtcFallback(parcel?.possessionDateLocal, parcel?.possessionDateUtc);
                return {
                    ...parcel,
                    customerName: parcel.customer.name,
                    isDistress: parcel.distress ? 'Yes' : 'No',
                    endPointAddress: parcel.endPointInfo
                        ? formatEndpointAddress(parcel.endPointInfo as ParcelEndpointInfo, true)
                        : '',
                    possessionDate: formattedPossessionDate,
                    deliveryDate: formatAndHandleUtcFallback(parcel?.deliveryDateLocal, parcel?.deliveryDateUtc),
                };
            });
        }

        return [];
    };

    const columns: ColumnDef<ParcelSearchGridData>[] = [
        {
            accessorKey: 'customerName',
            header: 'Customer',
            size: 80,
            meta: {
                innerHtml: ({ row, getValue }) => {
                    const { parcelEvents } = row.original;
                    const identifiers = parcelEvents.flatMap(
                        (event: v2_parcelSearchEvent) => event.identifier,
                    ) as v2_parcelSearchEvent['identifier'];

                    const sourceSystems = identifiers
                        .reduce<string[]>((prev, curr) => {
                            if (!prev.includes(curr.sourceSystemMemo ?? curr.sourceSystem)) {
                                prev.push(curr.sourceSystemMemo ?? curr.sourceSystem);
                            }
                            return prev;
                        }, [])
                        .join(', ');

                    // const toolTip = <Box className="parcel-sources">{sourceSystems}</Box>;

                    return (
                        <Tooltip arrow title={sourceSystems} placement="right">
                            <span>{getValue() as string}</span>
                        </Tooltip>
                    );
                },
            },
        },
        {
            accessorKey: 'endPointAddress',
            header: 'Endpoint',
            size: 250,
        },
        {
            accessorKey: 'freightType',
            header: 'Parcel Type',
            size: 80,
        },
        {
            accessorKey: 'isDistress',
            header: 'Distress',
            size: 80,
        },
        {
            accessorKey: 'possessionDate',
            header: 'Possession Date',
            size: 100,
            meta: {
                sortingOptionName: 'possessionDateLocal',
                headerHtml: (_props, displayText) => {
                    return (
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            {displayText}
                            <Tooltip arrow title={toolTipMessages.dates} placement="top">
                                <InfoTwoToneIcon />
                            </Tooltip>
                        </Box>
                    );
                },
            },
        },
        {
            accessorKey: 'deliveryDate',
            header: 'Delivery Date',
            size: 100,
            meta: {
                sortingOptionName: 'deliveryDateLocal',
                headerHtml: (_props, displayText) => {
                    return (
                        <>
                            {displayText}
                            <Tooltip arrow title={toolTipMessages.dates} placement="top">
                                <InfoTwoToneIcon />
                            </Tooltip>
                        </>
                    );
                },
            },
        },
        {
            accessorKey: 'parcelCode',
            header: 'Parcel Code',
            size: 100,
            meta: {
                innerHtml: ({ row }) => {
                    return (
                        <Box className="parcel-code-cell">
                            <ParcelCode parcelInfo={row.original} results={parcels ?? []} />
                        </Box>
                    );
                },
                headerHtml: (_props, displayText) => {
                    return (
                        <>
                            {displayText}
                            <Tooltip arrow title={toolTipMessages.parcelCode} placement="top">
                                <InfoTwoToneIcon />
                            </Tooltip>
                        </>
                    );
                },
            },
        },
    ];

    return (
        <>
            {parcels && parcels.length ? (
                <Card
                    className="results-grid auto-fill-height"
                    variant="outlined"
                    sx={{
                        boxShadow: 'none',
                        border: 'none',
                        backgroundColor: 'transparent',
                    }}
                >
                    <CardContent className="table-card auto-fill-height">
                        <FmtpTable
                            columns={columns}
                            data={mapData(parcels)}
                            fetchMoreOnBottomReached={updateFormSizeHandler}
                            setSelectedRows={setSelectedRows}
                            selectedRows={selectedRows}
                            onSortChange={onSortChange}
                            enableSorting={true}
                            totalResults={parcels.length}
                            initialSortingState={[
                                {
                                    id: sortColumn,
                                    desc: sortDirection === SortDirection.DESCENDING,
                                },
                            ]}
                            showLoading={true}
                            isFetching={isFetchingParcels}
                            enableExport={true}
                            exportFileName="FMTP_Parcel_Search_Results"
                        />
                    </CardContent>
                </Card>
            ) : null}
        </>
    );
}
